import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useStoryblokBridge } from '@storyblok/react';
import type { ISbStoryData } from '@storyblok/react';
import { canAccessInnerCircle, canAccessOuterCircle, UserRole } from '@whispers/sdk';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext.js';
import { useStoryblokPreview } from '../contexts/StoryblokPreviewContext.js';
import { useStory } from '../hooks/useStory.js';
import { CmsNavigationLinks, isCmsNavigationBlock } from './CmsNavigationLinks.js';

const LINK_CLASS =
  'flex w-full cursor-pointer items-center rounded-md px-3 py-2 text-sm text-text hover:bg-button-outline-hover-bg focus:bg-button-outline-hover-bg focus:outline-none';

const FallbackLinks = () => (
  <DropdownMenu.Item asChild>
    <Link to="/" className={LINK_CLASS}>
      Home
    </Link>
  </DropdownMenu.Item>
);

type NavigationContentProps = {
  initialStory: ISbStoryData;
  isNavigationPreview: boolean;
  user: ReturnType<typeof useAuth>['user'];
  onLogout: () => void;
};

const NavigationContent = ({
  initialStory,
  isNavigationPreview,
  user,
  onLogout,
}: NavigationContentProps) => {
  const [story, setStory] = useState(initialStory);

  useStoryblokBridge(story.id, (updated) => {
    setStory(updated);
  });

  const navigationBlock = isCmsNavigationBlock(story.content) ? story.content : null;
  const canAccessLink = (href: string) =>
    isNavigationPreview ||
    ((!href.startsWith('/inner-circle') || canAccessInnerCircle(user)) &&
      (!href.startsWith('/outer-circle') || canAccessOuterCircle(user)));

  return (
    <DropdownMenu.Root {...(isNavigationPreview ? { open: true } : {})}>
      <DropdownMenu.Trigger asChild>
        <button
          className="rounded-md p-2 hover:bg-button-outline-hover-bg focus:outline-none focus:ring-2 focus:ring-focus-ring"
          aria-label="Menu"
        >
          <svg
            className="h-6 w-6 text-text-secondary"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-50 min-w-[200px] rounded-lg border border-border bg-surface p-1 shadow-lg animate-slideDownAndFade"
          sideOffset={5}
          align="end"
        >
          {user && (
            <>
              <DropdownMenu.Item asChild>
                <div className="px-3 py-2 text-sm text-text-secondary">{user.email}</div>
              </DropdownMenu.Item>
              <DropdownMenu.Separator className="my-1 h-px bg-border" />
            </>
          )}

          {navigationBlock ? (
            <CmsNavigationLinks
              block={navigationBlock}
              linkClassName={LINK_CLASS}
              canAccessLink={canAccessLink}
            />
          ) : (
            <FallbackLinks />
          )}

          {user && (
            <>
              <DropdownMenu.Separator className="my-1 h-px bg-border" />

              <DropdownMenu.Item asChild>
                <Link to="/account" className={LINK_CLASS}>
                  Account
                </Link>
              </DropdownMenu.Item>

              {user.role === UserRole.enum.ADMIN && (
                <DropdownMenu.Item asChild>
                  <Link to="/admin/users" className={LINK_CLASS}>
                    Users
                  </Link>
                </DropdownMenu.Item>
              )}

              <DropdownMenu.Separator className="my-1 h-px bg-border" />

              <DropdownMenu.Item asChild>
                <button
                  onClick={onLogout}
                  className="flex w-full cursor-pointer items-center rounded-md px-3 py-2 text-sm text-error hover:bg-button-outline-hover-bg focus:bg-button-outline-hover-bg focus:outline-none"
                >
                  Sign out
                </button>
              </DropdownMenu.Item>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export const Navigation = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isPreviewMode } = useStoryblokPreview();
  const storyState = useStory('navigation');

  const isNavigationPreview =
    isPreviewMode && location.pathname.replace(/^\//, '') === 'navigation';

  const handleLogout = () => {
    void (async () => {
      await logout();
      void navigate('/sign-in');
    })();
  };

  return (
    <header>
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
        <Link to="/" className="font-serif text-lg text-text">
          <img
            src="/icon.png"
            alt="The Stone Circle"
            width={32}
            height={32}
            className="inline-block"
          />
        </Link>

        {storyState.status === 'found' ? (
          <NavigationContent
            initialStory={storyState.story}
            isNavigationPreview={isNavigationPreview}
            user={user}
            onLogout={handleLogout}
          />
        ) : (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                className="rounded-md p-2 hover:bg-button-outline-hover-bg focus:outline-none focus:ring-2 focus:ring-focus-ring"
                aria-label="Menu"
              >
                <svg
                  className="h-6 w-6 text-text-secondary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="z-50 min-w-[200px] rounded-lg border border-border bg-surface p-1 shadow-lg animate-slideDownAndFade"
                sideOffset={5}
                align="end"
              >
                <FallbackLinks />
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}
      </div>
    </header>
  );
};
